import React from 'react';
import Select, { SingleValue } from 'react-select';
import { Search, X } from 'lucide-react';
import {
  useResidentAddressPicker,
  AddressPickerPerson,
} from '../../hooks/useResidentAddressPicker';
import { getBlockLabel, getUnitLabel, CondoType } from '../../utils/terminology';

/**
 * Pick a morador (condomínio, by unidade) or a funcionário (empresa, by departamento).
 *
 * One component for every screen that needs it, because this flow was reimplemented per screen and
 * regressed every time. The three failures it is built against:
 *
 *   1. Querying people with `unit_id` and no `role_type` — condo-manager ignores the unit filter
 *      entirely in that case (person_handler.go:188) and answers with the whole customer, no
 *      error. The request parameters live in useResidentAddressPicker and are asserted by tests.
 *   2. Hiding the address once a person was chosen, taking away the context the operator had just
 *      used to find them. The filters stay on screen, always.
 *   3. Assuming "morador" — an empresa picks a funcionário by department and has no units at all.
 */

export interface PersonAddressPickerProps {
  customerId: string;
  /** 'condominio' | 'empresa' | 'fabrica' — decides morador-by-unit vs funcionário-by-department. */
  customerType?: string;
  /** 'casa' | 'apartamento' — decides Quadra/Lote vs Bloco/Unidade wording. */
  condoType?: CondoType;
  value: AddressPickerPerson | null;
  onChange: (person: AddressPickerPerson | null) => void;
  /** Overrides the automatic "Morador"/"Funcionário" heading. */
  label?: string;
  required?: boolean;
  disabled?: boolean;
  /** Shown under the heading when nothing is selected yet and a submit was attempted. */
  error?: string | null;
}

interface Option { value: string; label: string }

/**
 * Searchable combobox. Every address field is one of these rather than a native <select> so the
 * operator can TYPE to narrow it — a condo with 60 quadras and 200 lotes is unusable as a plain
 * dropdown. Same react-select setup the visitor modal uses.
 *
 * `inputId` matters: it is what the <label htmlFor> attaches to, so the field is reachable by its
 * name for both screen readers and tests.
 */
const AddressCombo: React.FC<{
  id: string;
  label: React.ReactNode;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  placeholder: string;
  disabled?: boolean;
}> = ({ id, label, value, options, onChange, placeholder, disabled }) => (
  <div>
    <label htmlFor={id} className="block text-xs text-gray-500 mb-1">{label}</label>
    <Select<Option>
      inputId={id}
      options={options}
      value={options.find(o => o.value === value) ?? null}
      onChange={(opt: SingleValue<Option>) => onChange(opt?.value ?? '')}
      placeholder={placeholder}
      noOptionsMessage={() => 'Nenhum resultado'}
      isDisabled={disabled}
      isClearable
      isSearchable
      menuPlacement="auto"
      styles={{
        control: base => ({ ...base, minHeight: 38, fontSize: 14 }),
        menu: base => ({ ...base, zIndex: 9999, fontSize: 14 }),
        menuPortal: base => ({ ...base, zIndex: 9999 }),
      }}
    />
  </div>
);

export const PersonAddressPicker: React.FC<PersonAddressPickerProps> = ({
  customerId,
  customerType,
  condoType,
  value,
  onChange,
  label,
  required = true,
  disabled = false,
  error,
}) => {
  const picker = useResidentAddressPicker({ customerId, customerType });

  const blockLabel = getBlockLabel(condoType as CondoType);
  const unitLabel = getUnitLabel(condoType as CondoType);
  const heading = label ?? (picker.isCompany ? 'Funcionário' : 'Morador');

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">
        {heading} {required && '*'}
      </label>

      {/* Rua + Número — only for casa-type condos, which address a lote by street. */}
      {!picker.isCompany && picker.hasStreets && (
        <div className="grid grid-cols-2 gap-3 mt-1 mb-2">
          <AddressCombo
            id="person-picker-street"
            label={<>Rua <span className="opacity-60">(filtro)</span></>}
            value={picker.streetId}
            options={picker.streets.map(st => ({ value: st.id, label: st.name }))}
            onChange={picker.setStreetId}
            placeholder="Digite para buscar..."
            disabled={disabled}
          />
          <AddressCombo
            id="person-picker-street-number"
            label={<>Número <span className="opacity-60">(filtro)</span></>}
            value={picker.streetNumber}
            options={picker.streetNumbers.map(n => ({ value: n, label: n }))}
            onChange={picker.setStreetNumber}
            placeholder={picker.streetId ? 'Digite o número...' : 'Selecione a rua'}
            disabled={disabled || !picker.streetId}
          />
        </div>
      )}

      {/* Quadra + Lote (condo) or Departamento (empresa). These stay visible after a person is
          picked — hiding them removes the context the operator used to find the person. */}
      {picker.isCompany ? (
        <div className="mt-1 mb-2">
          <AddressCombo
            id="person-picker-department"
            label="Departamento"
            value={picker.departmentId}
            options={picker.departments.map(d => ({ value: d.id, label: d.name }))}
            onChange={picker.setDepartmentId}
            placeholder="Digite para buscar..."
            disabled={disabled}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 mt-1 mb-2">
          <AddressCombo
            id="person-picker-block"
            label={blockLabel}
            value={picker.blockId}
            options={picker.blocks.map(b => ({ value: b.id, label: b.name }))}
            onChange={picker.setBlockId}
            placeholder="Digite para buscar..."
            disabled={disabled}
          />
          <AddressCombo
            id="person-picker-unit"
            label={unitLabel}
            value={picker.unitId}
            options={picker.units.map(u => ({ value: u.id, label: u.number }))}
            onChange={picker.setUnitId}
            placeholder={picker.blockId ? 'Digite para buscar...' : `Selecione ${String(blockLabel).toLowerCase()}`}
            disabled={disabled || !picker.blockId}
          />
        </div>
      )}

      {value ? (
        <div className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2 border border-gray-200">
          <span className="text-sm text-gray-900">
            {value.first_name} {value.last_name}
            {value.cpf && <span className="text-gray-400 ml-2 text-xs">{value.cpf}</span>}
          </span>
          <button
            type="button"
            onClick={() => onChange(null)}
            disabled={disabled}
            aria-label={`Remover ${heading.toLowerCase()} selecionado`}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={picker.search}
            onChange={e => picker.setSearch(e.target.value)}
            placeholder="Buscar por nome ou CPF..."
            disabled={disabled}
            className="pl-9 w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
          />

          {picker.visibleResidents.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-40 overflow-y-auto">
              {picker.visibleResidents.map(p => (
                <button
                  key={p.id}
                  type="button"
                  data-testid="person-result"
                  onClick={() => { onChange(p); picker.setSearch(''); }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                >
                  {p.first_name} {p.last_name}
                  {p.cpf && <span className="text-gray-400 ml-2">{p.cpf}</span>}
                </button>
              ))}
            </div>
          )}

          {/* Something was typed, people are loaded, nothing matches: say so rather than leaving
              an empty box that reads as a hung request. */}
          {picker.search.trim() && picker.residents.length > 0 && picker.visibleResidents.length === 0
            && !picker.loadingResidents && (
            <p className="text-xs text-gray-400 mt-1">
              Nenhum resultado para "{picker.search.trim()}".
            </p>
          )}
          {picker.loadingResidents && <p className="text-xs text-gray-400 mt-1">Buscando...</p>}
        </div>
      )}

      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};
